export const FIRST_TOUCH_KEY = 'first_touch_qs';
export const LAST_TOUCH_KEY = 'last_touch_qs';

const REF_ALIASES = ['ref', 'affiliate', 'aff', 'ref_id', 'r'] as const;
const CREATOR_ALIASES = ['creator', 'creator_id', 'cid', 'c'] as const;
const AD_CLICK_IDS = ['gclid', 'fbclid', 'ttclid'] as const;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

export type Attribution = Partial<{
  ref: string;
  creator: string;
  gclid: string;
  fbclid: string;
  ttclid: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  utm_term: string;
  captured_at: string;
}>;

function pickFirst(params: URLSearchParams, keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = params.get(key);
    if (value) return value;
  }
  return null;
}

export function readAttributionFromSearch(params: URLSearchParams): Attribution {
  const out: Attribution = {};

  const ref = pickFirst(params, REF_ALIASES);
  if (ref) out.ref = ref;

  const creator = pickFirst(params, CREATOR_ALIASES);
  if (creator) out.creator = creator;

  for (const key of AD_CLICK_IDS) {
    const value = params.get(key);
    if (value) out[key] = value;
  }

  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) out[key] = value;
  }

  return out;
}

export function hasAttribution(a: Attribution): boolean {
  return Object.keys(a).length > 0;
}

// Refs taboo-rag put on its links until PR #134 (2026-10-20), now UTM. They are
// not referrals, yet browsers still hold them in storage and old guide PDFs
// still carry them; on app.taboo.tv `ref` is the creator's FirstPromoter
// referral, so one of these must never stand in for it.
const LEGACY_AI_REFS: ReadonlySet<string> = new Set(['taboo-ai', 'guide', 'guide-pdf']);

function withoutLegacyRef(a: Attribution): Attribution {
  if (!a.ref || !LEGACY_AI_REFS.has(a.ref)) return a;
  const rest = { ...a };
  delete rest.ref;
  return rest;
}

// A touch without a referral keeps the previous one: a UTM-only link (Taboo
// Terra) replacing the whole last touch dropped the creator's `ref` and the
// sale landed with no creator.
export function mergeTouch(next: Attribution, prev: Attribution | null): Attribution {
  const touch = withoutLegacyRef(next);
  if (touch.ref) return touch;
  const prevRef = prev ? withoutLegacyRef(prev).ref : undefined;
  if (!prevRef) return touch;
  return { ...touch, ref: prevRef };
}

export function readStoredTouch(key: string): Attribution | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object') return parsed as Attribution;
    return null;
  } catch {
    return null;
  }
}

// Read attribution from localStorage. Last-touch wins (matches FP's last-click
// revenue attribution semantics); its `ref` falls back to first-touch's when
// it has none, which repairs last touches stored before mergeTouch existed.
// Returns {} on SSR, missing keys, or parse error.
export function readAttributionFromStorage(): Attribution {
  if (typeof window === 'undefined') return {};
  const first = readStoredTouch(FIRST_TOUCH_KEY);
  const last = readStoredTouch(LAST_TOUCH_KEY);
  if (last) return mergeTouch(last, first);
  return first ? withoutLegacyRef(first) : {};
}

// Clear first/last-touch attribution after a terminal conversion (signup or
// purchase). The ref has served its purpose — a subsequent visit should start
// a fresh attribution window, not inherit the old one.
export function clearAttribution(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(FIRST_TOUCH_KEY);
    window.localStorage.removeItem(LAST_TOUCH_KEY);
  } catch {
    // Safari private mode / quota exceeded — best-effort.
  }
}
